import { THEME, ThemeProvider } from '@vodacomsa/ns-react-components';
import { FormikInputField, Button } from '../../components/v3';
import {
  Form,
  Formik,
  Field
} from 'formik';
import React, {
  useEffect,
  useRef,
  useCallback
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
  useRecoilCallback
} from 'recoil';
import * as yup from 'yup';
import { StaticRoutes, AccountsRoutes } from '../../Router';
import {
  Footer,
  Header,
  Loader,
  OtpEntryModal,
  SupportPopup
} from '../../components';
import TextLink from '../../components/Link/TextLink';
import { useModal } from '../../components/modalController/useModal';
import { Typography } from '../../components/typography/typography';
import {
  CommonDetails,
  FormContainer,
  FormFieldWrapper,
  phoneEmailRegex
} from '../../constants';
import { ValidationErrorMessageMap } from '../../constants/validationErrorMessageMap';
import { findFirstValidationError } from '../../helpers/formik';
import {
  authDetail,
  continueClickedState,
  requestLoginOtpState,
  requestOtpLogin,
  userDetails,
  verifyLoginOtp,
  verifyLoginOtpState,
  useIsMobile,
  applicationsList
} from '../../recoil';
import { hydrateSessionContext } from '../../recoil/postLoginState';
import { useTealium } from '../../tealium/useTealium';
import { setTealiumUser } from '../../tealium/tealiumUser';
import styles from './Login.module.css';
import { msisdn } from './loginconstant';
import { OPFBROADCASTCHANNEL, AUTHBROADCASTMESSAGE } from '../../constants/constant.js';
import { useLoader } from '../../components/Loader/useLoader';

const validationSchema = yup.object({
  msisdn: yup.string().matches(phoneEmailRegex, 'Mobile number is not valid').required('Mobile number is required')
});

export const Login = () => {

  const userInfo = useRecoilValue(userDetails);
  const otpPending = useRef(false);
  const [otpResponse, setOtpRequest] = useRecoilState(requestOtpLogin);
  const [verifyOtpResponse, setOtpVerification] = useRecoilState(verifyLoginOtp);
  const [getAuthDetail, setAuthDetail] = useRecoilState(authDetail);
  const navigate = useNavigate();
  const location = useLocation();
  const [continueClicked, setContinueClicked] = useRecoilState(continueClickedState);
  const [openModal, closeModal] = useModal();
  const resetOtpRequestState = useResetRecoilState(requestLoginOtpState);
  const resetOtpResponseState = useResetRecoilState(verifyLoginOtpState);
  const resetLogin = useSetRecoilState(authDetail);
  const { track, events } = useTealium();
  setTealiumUser(userInfo);
  const isMobile = useRecoilValue(useIsMobile);
  const [openLoader, closeLoader] = useLoader();

  const checkPreSubmissionJourney = useRecoilCallback(({ snapshot }) => async () => {
    const list = await snapshot.getPromise(applicationsList);
    return !(Array.isArray(list) && list.length > 0);
  }, []);

  const hydrateSessionContextFromResponse = useRecoilCallback(({ set }) => (response) => {
    if (response?.routingDecision) {
      hydrateSessionContext(set, response);
    }
  }, []);

  const handleNavigation = useCallback(async () => {
    const loaderCloseDelay = 150;
    openLoader();
    try {
      closeOtpModal();

      const stored = (() => {
        try {
          const raw = sessionStorage.getItem('opf:sessionContext');
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })();

      const routingDecision = stored?.routingDecision;

      if (!routingDecision) {
        const isPreSubmissionJourney = await checkPreSubmissionJourney();
        const route = isPreSubmissionJourney ? StaticRoutes.FundingApplicationDetails : AccountsRoutes.dashboard;
        navigate(route);
        return;
      }

      switch (routingDecision) {
        case 'ACCOUNTS': {
          const { defaultBusinessId, defaultProductKey, allBusinessLenderStatuses } = stored;
          const productEntry = allBusinessLenderStatuses?.[defaultBusinessId]?.[defaultProductKey];
          const applicationId = productEntry?.applicationReference ?? productEntry?.applicationId;
          if (applicationId) {
            navigate(`/accounts/${applicationId}`);
          } else {
            navigate(AccountsRoutes.dashboard);
          }
          break;
        }
        case 'APPLICATIONS': {
          const { inProgressBusinessId } = stored;
          if (inProgressBusinessId) {
            navigate(`/applications/${inProgressBusinessId}`);
          } else {
            navigate(AccountsRoutes.dashboard);
          }
          break;
        }
        case 'ONBOARDING':
        default:
          navigate(StaticRoutes.FundingApplicationDetails);
          break;
      }
    } finally {
      setTimeout(closeLoader, loaderCloseDelay);
    }
  }, [checkPreSubmissionJourney]);

  let formTouched = false;

  const handleFormFocus = () => {
    if (formTouched) {
      return;
    }

    formTouched = true;
    track(events.login.formStart());
  };

  useEffect(() => {
    track(events.login.pageView());
  }, []);

  useEffect(() => {
    const channel = new BroadcastChannel(OPFBROADCASTCHANNEL);

    channel.onmessage = (event) => {
      if (event.data?.type === AUTHBROADCASTMESSAGE) {
        const newAuthDetail = event.data.data;
        setAuthDetail(newAuthDetail);

        if (newAuthDetail?.token) {
          handleNavigation();
        }
      }
    };

    return () => {
      channel.close();
    };
  }, []);

  const sendOtpRequest = (userDetailsInput) => {

    if (!otpPending.current) {
      otpPending.current = true;
      track(events.login.continue());
      setOtpRequest(userDetailsInput);
    }
  };

  useEffect(() => {
    if (otpResponse) {
      otpPending.current = false;
    }
  }, [otpResponse]);

  useEffect(() => {
    if (verifyOtpResponse?.token) {
      track(events.login.loginComplete());
      hydrateSessionContextFromResponse(verifyOtpResponse);
      setAuthDetail(verifyOtpResponse);
    }
  }, [verifyOtpResponse, setAuthDetail]);

  useEffect(() => {
    if (getAuthDetail?.token) {
      handleNavigation();
    }
  }, [getAuthDetail]);

  const DetailsEntry = () => {

    const formRef = useRef();

    const handleSubmit = () => {
      if (formRef.current) {
        track(events.login.continue());
        formRef.current.handleSubmit();
      }
    };

    const submitDetails = (formValues) => {
      setContinueClicked(true);
      sendOtpRequest({ contact: formValues.msisdn });
    };

    const goToTermsConditionsPage = () => {
      track(events.login.termsAndConditions());
      window.open(CommonDetails.vodalendTermsConditions, '_blank');
    };

    const goToPrivacyPolicyPage = () => {
      track(events.registration.TermConditionPrivacyPolicy('privacy statement'));
      window.open(CommonDetails.vodalendPrivacyPolicy, '_blank');
    };

    const goToRegistration = () => {
      track(events.login.notRegistered());
      navigate(StaticRoutes.registration);
    };

    const handleFormikTouchedErrors = (touched, errors) => {
      const errorMessage = findFirstValidationError(errors, touched);

      if (!errorMessage) {
        return;
      }

      track(events.error('form', location.pathname, errorMessage));
    };
    return (
      <div className={styles.loginFormWrapper}>

        <Typography align={'center'} level={3} family={'medium'} className={styles.heading}
          size={'h3'}>
          {!isMobile ? 'Log in to your account' : 'Login'}
        </Typography>

        <Typography align={'center'} weight={'medium'} className={styles.subHeading} spacing={3} size={'b1'}>
          Not registered?&nbsp;
          <span
            className={styles.boldRegistrationLink}
            onClick={goToRegistration}
            onKeyDown={goToRegistration}
            role='presentation'
          >
            Get started
          </span>
        </Typography>

        <ThemeProvider theme={THEME.revamp}>
          <Formik
            initialValues={userInfo}
            onSubmit={submitDetails}
            validationSchema={validationSchema}
            validateOnMount={true}
            innerRef={formRef}
          >
            {({ errors, touched, isValid }) => {
              handleFormikTouchedErrors(touched, errors);

              return (
                <div className={styles.formik}>
                  <Form style={FormContainer} onFocus={handleFormFocus}>
                    <div style={FormFieldWrapper}>
                      <div className={styles.formContainerInput}>
                        <Field component={FormikInputField} {...msisdn} overrideHintColor={true} />
                      </div>
                    </div>

                    <Footer
                      rightTitle={'Continue'}
                      rightCallback={errors.msisdn ? null : handleSubmit}
                      containerStyle={{ marginTop: '50px', paddingTop: '18px' }}
                    />

                    <div className={styles.terms}>
                      {isValid ? (
                        <>
                          By continuing, I agree to the Vodacom{' '}
                          <TextLink linkText='Terms and conditions' onClick={goToTermsConditionsPage} />
                        </>
                      ) : (
                        <>
                          By continuing, I agree to the Vodacom{' '}
                          <TextLink linkText='Privacy statement' onClick={goToPrivacyPolicyPage} />
                        </>
                      )}
                    </div>
                  </Form>
                </div>
              );
            }}
          </Formik>
        </ThemeProvider>

        <SupportPopup />
      </div>
    );
  };

  const closeOtpModal = () => {
    closeModal();
    resetOtpResponseState();
    resetOtpRequestState();
    setContinueClicked(false);
  };

  const resetAllResponse = () => {
    resetOtpResponseState();
    resetOtpRequestState();
  };

  const submitOtp = (otp) => {
    track(events.otp.continue());
    setOtpVerification(otp);
  };

  const resendOtp = () => {
    track(events.otp.resend());
    setOtpRequest(null);
  };

  useEffect(() => {

    const errorMsg = ValidationErrorMessageMap[verifyOtpResponse?.message] ||
      ValidationErrorMessageMap[otpResponse?.message] || otpResponse?.error?.message || verifyOtpResponse?.error?.message;
    const otpErrorCode = otpResponse?.message;
    if (errorMsg) {

      if (errorMsg === ValidationErrorMessageMap.InvalidLoginRef) {
        resetLogin(null);
        openModal({
          title: 'Error',
          children: <div dangerouslySetInnerHTML={{ __html: 'Please Try again' }} />,
          type: 'error',
          onCloseModal: closeOtpModal,
          actions: [<Button key="close" onClick={closeOtpModal}>Close</Button>]
        });
        return;
      }

      if (errorMsg === 'The request was sent but no response was received') {

        openModal({
          title: 'Error',
          children: <div dangerouslySetInnerHTML={{ __html: ValidationErrorMessageMap[errorMsg] }} />,
          type: 'error',
          onCloseModal: closeOtpModal,
          actions: [<Button key="close" onClick={closeOtpModal}>Try again</Button>]
        });
        return;
      }

      openModal({
        title: 'Error',
        children: <div className={otpErrorCode === 'InvalidCredentials' ? styles.errorMessageStyle : ''} dangerouslySetInnerHTML={{ __html: errorMsg }} />,
        type: 'error',
        onCloseModal: closeOtpModal,
        actions: [<Button key="close" onClick={closeOtpModal}>Close</Button>]
      });
      return;
    }

    if (continueClicked) {
      openModal({
        children: <OtpEntryModal
          maskedContact={otpResponse?.maskedContact}
          verifyOtpResponse={verifyOtpResponse}
          otpResponse={otpResponse}
          submitOtp={submitOtp}
          resendOtp={resendOtp}
          resetAllResponse={resetAllResponse}
          onView={() => track(events.otp.pageView())}
          closeOtpModal={closeOtpModal}
        />,
        onCloseModal: closeOtpModal
      });
      return;
    }
  }, [otpResponse, continueClicked, verifyOtpResponse]);

  return (
    <>
      {verifyOtpResponse?.token &&
        <Loader />
      }
      {!verifyOtpResponse?.token &&
        <div className={styles.loginContainer}>
          <Header />
          <DetailsEntry />
        </div>
      }
    </>
  );
};
